// trial-banner.js — trial-ending banner + cross-subdomain auth cookies.
//
// Three pieces that all sit near each other in the "billing" UX
// surface but don't fit in pages/settings.js or analytics.js:
//
//   writeAuthHintCookie(tier)
//     Cross-subdomain auth hint cookie for the marketing site at
//     agentcollision.com (not auth-sensitive — just a UX nudge so
//     the marketing nav can show "Dashboard" instead of "Sign In").
//
//   clearAuthHintCookie()
//     Inverse of the above. Called on sign-out + on session expiry.
//
//   renderTrialBanner(billing)
//     Shows a 3-day-or-less trial-ending banner. Dismissable per
//     session (sessionStorage). On the last day uses stronger
//     "today!" copy. `?test_trial_banner=N` URL param forces N
//     days remaining for QA without a real Stripe trial.
//
// Plus a module-init document-level click handler for the banner's
// CTA + dismiss buttons. Same idempotency-guarded pattern as
// team-settings.js and analytics.js use.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K-d).
import { navigate } from "./router.js";

export function writeAuthHintCookie(tier) {
  try {
    const isLocal =
      location.hostname === "localhost" || location.hostname === "127.0.0.1";
    if (isLocal) return;
    const val = encodeURIComponent(
      JSON.stringify({ signed_in: true, tier: tier || "solo", ts: Date.now() }),
    );
    // Domain=.agentcollision.com so agentcollision.com can read it
    document.cookie =
      "ac_session_hint=" +
      val +
      "; Domain=.agentcollision.com; Path=/; Max-Age=604800; SameSite=Lax; Secure";
  } catch {}
}

export function clearAuthHintCookie() {
  try {
    if (
      location.hostname === "localhost" ||
      location.hostname === "127.0.0.1"
    )
      return;
    document.cookie =
      "ac_session_hint=; Domain=.agentcollision.com; Path=/; Max-Age=0; SameSite=Lax; Secure";
  } catch {}
}

/**
 * Render the trial-ending banner based on billing status.
 * Gate: ONLY users actively trialing AND within 3 days of trial_end see the
 * banner. Free Solo users and paid-active users never see it.
 *
 * The banner is dismissable per-session (sessionStorage so it comes back after
 * the user closes the tab — we want to keep reminding them, just not every
 * page load). On the last day it shows a stronger "today!" state.
 */
export function renderTrialBanner(billing) {
  const banner = document.getElementById("trial-ending-banner");
  if (!banner) return;

  // Dev/QA escape hatch: `?test_trial_banner=N` forces the banner to render
  // as if the user is trialing with N days left, regardless of real billing.
  // Lets you verify visuals without needing a real Stripe trial in the right
  // state. Doesn't affect anything server-side; resets on next page load.
  // Example:  https://dashboard.agentcollision.com/?test_trial_banner=2
  const testParam = new URLSearchParams(location.search).get(
    "test_trial_banner",
  );
  if (testParam !== null) {
    const days = Math.max(0, parseInt(testParam, 10) || 0);
    const fakeTrialEnd = new Date(Date.now() + days * 86400000).toISOString();
    billing = {
      ...(billing || {}),
      is_trialing: true,
      trial_end: fakeTrialEnd,
      tier: billing && billing.tier !== "solo" ? billing.tier : "startup",
    };
  }

  if (!billing || !billing.is_trialing || !billing.trial_end) {
    banner.style.display = "none";
    return;
  }
  const dismissedKey = "ac-trial-banner-dismissed-" + billing.trial_end;
  let dismissed = false;
  // Test mode bypasses dismissal so the banner always renders.
  if (testParam === null) {
    try {
      dismissed = sessionStorage.getItem(dismissedKey) === "1";
    } catch (_) {}
  }
  if (dismissed) {
    banner.style.display = "none";
    return;
  }
  const msLeft = new Date(billing.trial_end) - new Date();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  if (daysLeft > 3) {
    banner.style.display = "none";
    return;
  }
  const title = document.getElementById("trial-banner-title");
  const body = document.getElementById("trial-banner-body");
  const tierLabel = billing.tier === "enterprise" ? "Enterprise" : "Startup";
  if (daysLeft === 0) {
    title.textContent = "Your trial ends today";
    body.textContent =
      "Add a payment method now to keep " +
      tierLabel +
      " features. Otherwise your team drops to Solo automatically.";
  } else if (daysLeft === 1) {
    title.textContent = "Your trial ends tomorrow";
    body.textContent =
      "Add a payment method to keep " +
      tierLabel +
      " features, or your team moves to Solo automatically.";
  } else {
    title.textContent = "Your trial ends in " + daysLeft + " days";
    body.textContent =
      "Add a payment method to keep " +
      tierLabel +
      " features, or your team moves to Solo automatically.";
  }
  banner.style.display = "";
}

// ---- module init: wire CTA + dismiss buttons ----
//
// Same idempotency-guard pattern as the legacy `_trialBannerWired`
// flag (kept compatible so a re-import or double-load of this module
// can't stack two listeners on document).
function wireListeners() {
  if (window._trialBannerWired) return;
  window._trialBannerWired = true;
  document.addEventListener("click", (e) => {
    if (e.target && e.target.id === "trial-banner-cta") {
      navigate("settings");
      setTimeout(() => {
        const picker = document.getElementById("billing-plan-picker");
        if (picker)
          picker.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return;
    }
    if (e.target && e.target.id === "trial-banner-dismiss") {
      const billing = window._billing;
      if (billing && billing.trial_end) {
        try {
          sessionStorage.setItem(
            "ac-trial-banner-dismissed-" + billing.trial_end,
            "1",
          );
        } catch (_) {}
      }
      const banner = document.getElementById("trial-ending-banner");
      if (banner) banner.style.display = "none";
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
